import React, { useMemo, useRef, useState } from 'react'
import CX from 'classnames'
import { Classes, Intent, Spinner, Tag } from '@blueprintjs/core'
import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FailableImage, ZoomableGallery } from './SortableList'
import ImageHelpers from './Helpers'
import { BaseSortableImageListProps } from './sortableImageListTypes'
import { FadingScrollContainer } from './FadingScrollContainer'
import useBreakpoint from '../../hooks/useBreakpoint'
import { getSortableImageId } from './sortableImageIds'

interface MinimalSortableItemProps {
  id: string
  loading: boolean
  url: string // the url of the image, or preview url
  ii: number
  rotation: number
  progress: number
  isDisabled: boolean
  setCurrentImageIndex: (index: number) => void
  hidden?: boolean
  failed?: boolean
}

const MinimalSortableItem: React.FC<MinimalSortableItemProps> = ({
  id,
  loading,
  progress,
  url,
  rotation,
  ii, // the item index
  isDisabled,
  setCurrentImageIndex,
  hidden = false,
  failed = false
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isDisabled
  })

  const sortableStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.88 : 1,
    zIndex: isDragging ? 2 : undefined,
    touchAction: 'none'
  }

  const hackRotation = React.useMemo(() => {
    return ImageHelpers.hackRotationFn(url)
  }, [url])
  const className = CX('img-container-minimal', hidden ? '' : 'active')

  if (failed) {
    return (
      <div ref={setNodeRef} className={className} style={sortableStyle} {...listeners} {...attributes}>
        <p className={Classes.TEXT_MUTED}>Image Upload Failed</p>
      </div>
    )
  }

  return (
    <div
      ref={setNodeRef}
      className={className}
      style={sortableStyle}
      {...listeners}
      {...attributes}
      onClick={() => !loading && !isDisabled && !hidden && setCurrentImageIndex(ii)}
    >
      {loading ? (
        <Spinner value={progress} />
      ) : (
        <FailableImage
          src={url}
          className="img-selection-img"
          alt={`number ${ii + 1}`}
          style={{
            transform: `translatez(0) rotate(${-Math.abs(hackRotation - rotation || 0)}deg)`
          }}
        />
      )}
      {ii === 0 && !loading && !hidden && (
        <Tag className="main-img-tag-minimal" intent={Intent.PRIMARY}>
          Main Image
        </Tag>
      )}
    </div>
  )
}

export const MinimalSortableImageList = ({
  targetElementSelectorForSticky = '#image-uploader-container',
  items,
  isDisabled = false,
  children,
  onSortEnd,
  distance = 10
}: BaseSortableImageListProps) => {
  const [currentImageIndex, setCurrentImageIndex] = useState<number | undefined>()

  const innerContainerRef = useRef<HTMLDivElement>()

  const stickyBreakpoint = useBreakpoint(targetElementSelectorForSticky)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: distance ?? 10 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  const itemIds = useMemo(() => items.map((it, i) => getSortableImageId(it, i)), [items])

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onSortEnd) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = itemIds.indexOf(String(active.id))
    const newIndex = itemIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    onSortEnd({ oldIndex, newIndex })
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
      <FadingScrollContainer innerContainerRef={innerContainerRef} hidden={!stickyBreakpoint}>
        <div ref={innerContainerRef} className={CX('image-gallery-container-minimal')}>
          <ZoomableGallery
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            items={items}
          />
          {children}
          <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
            {items.map((f, i) => (
              <MinimalSortableItem
                key={itemIds[i]}
                id={itemIds[i]}
                loading={f.loading}
                url={f.url || f.preview}
                ii={i}
                rotation={f.rotation}
                progress={f.progress}
                isDisabled={isDisabled}
                hidden={!stickyBreakpoint}
                setCurrentImageIndex={setCurrentImageIndex}
                failed={f.failed}
              />
            ))}
          </SortableContext>
        </div>
      </FadingScrollContainer>
    </DndContext>
  )
}
